import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { GridColDef, DataGrid, GridValueGetter, GridRenderCellParams } from '@mui/x-data-grid';
import { Button } from '@mui/material';
import { appVerdictName } from '../../../util/enumerations';
import { VerdictDTO } from '../../../DTO/VerdictDTO';
import { APIService } from '../../../services/apiService';
import { ROUTES } from '../../../constants';

interface VerdictHistoryProps {
	courseMappingHeadId?: number;
	verdict: VerdictDTO | null;
	setVerdict: React.Dispatch<React.SetStateAction<VerdictDTO | null>>;
	height?: string;
	readonly?: boolean;
}

const api = new APIService();

const VerdictHistory: React.FC<VerdictHistoryProps> = ({
	courseMappingHeadId,
	verdict,
	setVerdict,
	height = '400px',
	readonly = false
}) => {
	const [mappingHistoryLocal, setMappingHistoryLocal] = useState<VerdictDTO[] | null>(null);
	const verdicts = mappingHistoryLocal || [];

	const getMappingHistory = useCallback(() => {
		if (!courseMappingHeadId) return;

		if (verdicts.length === 0) {
			api.getMappingHistory(courseMappingHeadId).then((data) => {
				setMappingHistoryLocal(data);
			});
		}
	}, [courseMappingHeadId, verdicts.length]);

	useEffect(() => {
		getMappingHistory();
	}, [getMappingHistory]);

	const copyVerdict = useCallback(
		(copiedVerdict: VerdictDTO) => {
			if (!verdict) return;
			setVerdict(
				(prevVerdict) =>
					({
						...prevVerdict,
						note: copiedVerdict.note,
						status: copiedVerdict.status,
					} as VerdictDTO)
			);
		},
		[verdict, setVerdict]
	);

	const colDefs: GridColDef[] = useMemo(() => {
		const baseColumns: GridColDef[] = [
			{
				field: 'status',
				headerName: 'Status',
				type: 'string',
				width: 300,
				valueGetter: ((_value, row: VerdictDTO) =>
					row ? appVerdictName(row.status) : '') as GridValueGetter<VerdictDTO, string>,
			},
			{
				field: 'note',
				headerName: 'Note',
				type: 'string',
				width: 300,
			},
			{
				field: 'created',
				type: 'dateTime',
				headerName: 'Created',
				width: 200,
				valueGetter: ((value, row: VerdictDTO) =>
					row?.created ? new Date(row.created) : null) as GridValueGetter<VerdictDTO, Date | null>,
			},
			{
				field: 'createdBy',
				headerName: 'Created by',
				type: 'string',
				width: 150,
			},
		];

		const actionColumns: GridColDef[] = [];

		if (!readonly) {
			actionColumns.push({
				field: 'copy',
				headerName: ' ',
				width: 100,
				renderCell: (params: GridRenderCellParams<VerdictDTO>) => (
					<Button
						variant="contained"
						color="secondary"
						onClick={() => copyVerdict(params.row)}
					>
						Copy
					</Button>
				),
			});
		}

		actionColumns.push({
			field: 'edit',
			headerName: ' ',
			width: 180,
			renderCell: (params: GridRenderCellParams<VerdictDTO>) => (
				<Button
					variant="contained"
					color="primary"
					component={Link}
					to={`${ROUTES.APPLICATION}/${params.row.applicationHeadId}`}
					target="_blank"
					rel="noopener noreferrer"
				>
					Open application
				</Button>
			),
		});

		return [...baseColumns, ...actionColumns];
	}, [copyVerdict, readonly]);

	return (
		<div style={{ height }}>
			<DataGrid
				hideFooterPagination={true}
				columns={colDefs}
				hideFooter={true}
				rows={verdicts}
			/>
		</div>
	);
};

export default VerdictHistory;